@if (loading$ | async) {
  <app-loading title="Initializing Application..." message="Please wait, we're getting things ready."></app-loading>
} @else {
  @if (needsSignIn$ | async) {
    <!-- Issue #1958: sign-in has stopped and owes an explanation — either the
         credential kept failing to arrive back here, or the sign-in helper
         cannot be reached from this origin. Both used to be invisible: the
         first looped silently, the second claimed a server fault. -->
    @if (signInBlocked$ | async; as blocked) {
      <div class="signin-message signin-blocked" data-testid="sign-in-blocked" role="alert">
        <h1 class="signin-prompt-title">{{ blocked.title }}</h1>
        <p class="signin-prompt-notice signin-blocked-message" data-testid="sign-in-blocked-message">
          {{ blocked.message }}
        </p>
        <dl class="signin-blocked-hosts">
          <dt>This page</dt>
          <dd data-testid="sign-in-blocked-origin">{{ blocked.pageOrigin }}</dd>
          <dt>Sign-in handled by</dt>
          <dd data-testid="sign-in-blocked-auth-domain">{{ blocked.authDomain }}</dd>
        </dl>
        <button
          mat-raised-button
          color="primary"
          class="signin-prompt-action"
          data-testid="sign-in-blocked-retry"
          (click)="retrySignIn()"
        >
          <mat-icon>refresh</mat-icon>
          Try signing in again
        </button>
      </div>
    } @else if (signInRequiresGesture$ | async) {
      <!-- Off the primary host the credential comes back through a popup, and a
           popup can only be opened from a click, so ask for one instead of
           showing a spinner that will never resolve. -->
      <div class="signin-message signin-prompt">
        <h1 class="signin-prompt-title">Sign in to PermitProof</h1>
        <p class="signin-prompt-body">Continue with your Google account to open your projects.</p>
        @if (signInNotice$ | async; as notice) {
          <p class="signin-prompt-notice" role="alert">{{ notice }}</p>
        }
        <button mat-raised-button color="primary" class="signin-prompt-action" (click)="signIn()">
          <mat-icon>login</mat-icon>
          Sign in with Google
        </button>
      </div>
    } @else {
      <div class="signin-message">
        <app-loading title="Sign In Required" message="Redirecting to Google sign-in..."></app-loading>
      </div>
    }
  } @else {
    <!-- Only show UI elements when NOT on unauthorized/error routes (Issue #304) -->
    @if ((isUnauthorizedRoute$ | async) === false) {
      <app-global-progress></app-global-progress>
      <!-- Admin Top App Bar for /admin routes -->
      @if ((isAdminRoute$ | async) === true) {
        <app-admin-top-app-bar
          [user$]="user$"
          (toggleDrawer)="toggleDrawer()"
          (switchAccount)="switchAccount()"
          (signOut)="signOut()"
        ></app-admin-top-app-bar>
      }
      <!-- Project Top App Bar for /projects/* routes -->
      @if ((isProjectRoute$ | async) === true) {
        <app-project-top-app-bar
          [user$]="user$"
          [showProjectUI]="(showProjectUI$ | async) || false"
          [currentProjectId]="(currentProjectId$ | async) || null"
          [currentProjectTitle]="(currentProjectTitle$ | async) || null"
          (toggleDrawer)="toggleDrawer()"
          (toggleChat)="toggleChat()"
          (openShareDialog)="openShareDialog()"
          (switchAccount)="switchAccount()"
          (createNewProject)="createNewProject()"
          (openProjectSelector)="openProjectSelector()"
          (openProjectCopyDialog)="openProjectCopyDialog()"
          (openProjectDeleteDialog)="openProjectDeleteDialog()"
          (openProjectSettings)="openProjectSettings()"
          (navigateToTasks)="navigateToTasks()"
          (signOut)="signOut()"
        ></app-project-top-app-bar>
      }
      <!-- Billing Top App Bar for /billing routes -->
      @if ((isBillingRoute$ | async) === true) {
        <app-billing-top-app-bar
          [user$]="user$"
          (switchAccount)="switchAccount()"
          (signOut)="signOut()"
          (refresh)="onRefreshBilling()"
        ></app-billing-top-app-bar>
      }
      <!-- Landing Top App Bar for home and other generic routes -->
      @if ((isLandingRoute$ | async) === true) {
        <app-landing-top-app-bar
          [user$]="user$"
          (switchAccount)="switchAccount()"
          (signOut)="signOut()"
        ></app-landing-top-app-bar>
      }
      <!-- Chat Feature (Issue #263) - Hidden on unauthorized routes -->
      <app-chat></app-chat>
    }
    <!-- Router outlet handles all routes including /unauthorized -->
    <div class="main-content-area">
      <router-outlet></router-outlet>
    </div>
  }
}
